import React, { useState, useEffect } from 'react';
import {
  FileText,
  Calendar,
  User,
  CheckCircle,
  XCircle as XCircleIcon,
} from 'lucide-react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { DocumentMaster } from '../../../services/documentMasterService';
import { userService } from '../../../services/userService';

interface DocumentDetailViewProps {
  document: DocumentMaster | null;
}

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateString: string | undefined | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Row: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="flex items-start gap-2 py-1.5 text-sm">
    {icon && <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>}
    <div className="min-w-0 flex-1 grid grid-cols-[100px_1fr] gap-2 sm:grid-cols-[110px_1fr]">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="text-gray-900 truncate">{value ?? '—'}</span>
    </div>
  </div>
);

const DocumentDetailView: React.FC<DocumentDetailViewProps> = ({ document }) => {
  const [createdByName, setCreatedByName] = useState<string>('Loading...');
  const [updatedByName, setUpdatedByName] = useState<string>('');

  useEffect(() => {
    const fetchUserNames = async () => {
      if (!document) return;

      // Fetch createdBy user name
      if (document.createdBy) {
        try {
          const userId = typeof document.createdBy === 'string' ? document.createdBy : document.createdBy.id;
          if (userId) {
            const user = await userService.getUserById(userId);
            setCreatedByName(user.name || 'Unknown User');
          } else if (typeof document.createdBy === 'object' && document.createdBy?.name) {
            setCreatedByName(document.createdBy.name);
          }
        } catch (error) {
          console.error('Failed to fetch createdBy user:', error);
          if (typeof document.createdBy === 'object' && document.createdBy?.name) {
            setCreatedByName(document.createdBy.name);
          } else {
            setCreatedByName('Unknown User');
          }
        }
      }

      // Fetch updatedBy user name
      if (document.updatedBy) {
        try {
          const userId = typeof document.updatedBy === 'string' ? document.updatedBy : document.updatedBy.id;
          if (userId) {
            const user = await userService.getUserById(userId);
            setUpdatedByName(user.name || 'Unknown User');
          } else if (typeof document.updatedBy === 'object' && document.updatedBy?.name) {
            setUpdatedByName(document.updatedBy.name);
          }
        } catch (error) {
          console.error('Failed to fetch updatedBy user:', error);
          if (typeof document.updatedBy === 'object' && document.updatedBy?.name) {
            setUpdatedByName(document.updatedBy.name);
          } else {
            setUpdatedByName('Unknown User');
          }
        }
      }
    };

    fetchUserNames();
  }, [document]);

  if (!document) return null;

  const isActive = document.status === 'Active';

  return (
    <div className="flex flex-col gap-4 p-1">
      {/* Compact header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <div className="w-10 h-10 rounded-lg bg-surface-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
          <FileText size={20} className="text-gray-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {document.documentName}
          </h3>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <Tag
              value={isActive ? 'Active' : 'Inactive'}
              severity={isActive ? 'success' : 'danger'}
              icon={isActive ? <CheckCircle size={12} /> : <XCircleIcon size={12} />}
              className="text-xs py-0"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {/* Document Information */}
        <Card className="!shadow-none !border !border-gray-200 !rounded-lg overflow-hidden">
          <div className="p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <FileText size={12} /> Document Information
            </div>
            <div className="space-y-0">
              <Row label="Name" value={document.documentName} icon={<FileText size={14} />} />
              {document.documentDescription && (
                <Row label="Description" value={document.documentDescription} icon={<FileText size={14} />} />
              )}
              <Row 
                label="Status" 
                value={
                  <Tag
                    value={document.status}
                    severity={isActive ? 'success' : 'danger'}
                    className="text-xs py-0"
                  />
                } 
                icon={<CheckCircle size={14} />} 
              />
            </div>
          </div>
        </Card>

        {/* Timeline */}
        <Card className="!shadow-none !border !border-gray-200 !rounded-lg overflow-hidden">
          <div className="p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Calendar size={12} /> Timeline
            </div>
            <div className="space-y-0">
              <Row 
                label="Created By" 
                value={createdByName} 
                icon={<User size={14} />} 
              />
              <Row 
                label="Created At" 
                value={formatDateTime(document.createdAt)} 
                icon={<Calendar size={14} />} 
              />
              {document.updatedAt && (
                <>
                  {updatedByName && (
                    <Row 
                      label="Updated By" 
                      value={updatedByName} 
                      icon={<User size={14} />} 
                    />
                  )}
                  <Row 
                    label="Updated At" 
                    value={formatDateTime(document.updatedAt)} 
                    icon={<Calendar size={14} />} 
                  />
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DocumentDetailView;
